
   
/* src/components/ProjectsSection.tsx
   – Built with Swiper 10
   – Click any slide → advance just enough to bring the next hidden slides into view
────────────────────────────────────────────────── */
import React, { useState, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperCore } from "swiper";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";

/* cursor images (100 × 100 px) */
import ChevronLeftIcon  from "../../assets/arrowproj_100.png";
import ChevronRightIcon from "../../assets/arrowproj_100.png";

/* ——— types ——— */
type Project = {
  name: string;
  location: string;
  image?: string;
};

type ProjectsSectionProps = {
  projects: Project[];
  buildingImage: string;
};

/* ——— component ——— */
const ProjectsSection: React.FC<ProjectsSectionProps> = ({ projects }) => {
  /* cursor state */
  const [cursor, setCursor] = useState<"left" | "right" | "default">("default");
  const prevXRef = useRef<number | null>(null);
  const skipNextMouseMove = useRef(false);

  /* Swiper instance */
  const [swiper, setSwiper] = useState<SwiperCore | null>(null);

  /* ───────────── cursor helpers ───────────── */
  const getCursor = () => {
    if (cursor === "left")  return `url(${ChevronLeftIcon}) 10 10, auto`;
    if (cursor === "right") return `url(${ChevronRightIcon}) 10 10, auto`;
    return "auto";
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    const x = e.clientX;
    if (skipNextMouseMove.current) {
      skipNextMouseMove.current = false;
      prevXRef.current = x;
      return;
    }
    if (prevXRef.current !== null) {
      setCursor(x > prevXRef.current ? "right" : "left");
    }
    prevXRef.current = x;
  };

  /* ──────────── click-to-advance helper ──────────── */
  const goToNextHidden = () => {
    if (!swiper) return;

    /* slides that fit NOW (works with slidesPerView="auto") */
    const perView =
      swiper.params.slidesPerView === "auto"
        ? swiper.slidesPerViewDynamic()
        : (swiper.params.slidesPerView as number);

    /* first slide index for the next “page” */
    const nextIndex = Math.min(
      swiper.activeIndex + perView,
      swiper.slides.length - perView
    );

    swiper.slideTo(nextIndex, 500); // 500-ms animation
  };

  return (
    <section className="bg-white py-[120px] px-6 md:px-20">
      {/* ------------ heading ------------ */}
      <header className="mb-[60px]">
        <p className="font-inter font-semibold text-[22px] leading-[150%] tracking-[-0.005em] bg-gradient-to-t from-[#46C1BF] to-[#46C1BF] bg-clip-text text-transparent mb-4">
          Our Projects //
        </p>
        <h2 className="capitalize font-utility leading-[120%] tracking-[-0.01em] text-[#011621] font-medium sm:text-[54px] text-[30px]">
          Where Vision Becomes Reality
        </h2>
      </header>

      {/* ------------ carousel ------------ */}
      <div
        onMouseMove={handleMouseMove}
        // eslint-disable-next-line
        onMouseLeave={() => (setCursor("default"), (prevXRef.current = null))}
        onMouseDown={() => (skipNextMouseMove.current = true)}
        onMouseUp={() => (skipNextMouseMove.current = true)}
        style={{ cursor: getCursor() }}
      >
        <Swiper
          onSwiper={setSwiper}                       /* store instance */
          modules={[Navigation]}
          navigation={{ nextEl: ".swiper-button-next", prevEl: ".swiper-button-prev" }}
          spaceBetween={32}
          slidesPerView="auto"
        >
          {projects.map((project, i) => (
            <SwiperSlide
              key={i}
              onClick={goToNextHidden}              /* ⇢ advance one page */
              className="group !flex !flex-col !w-[326px]"
            >
              {/* image */}
              <div
                className="relative w-[326px] aspect-[341/512] overflow-hidden
                           [clip-path:polygon(0_0,100%_0,100%_100%,0_100%)]
                           group-hover:[clip-path:polygon(0_0,100%_0,100%_90%,90%_100%,0_100%)]
                           transition-[clip-path] duration-300"
              >
                <img
                  src={project.image}
                  alt={project.name}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                  width={326}
                  height={512}
                />
                <div className="absolute inset-0 bg-[#011621] opacity-30 pointer-events-none" />
              </div>

              {/* text */}
              <div className="mt-8 w-[326px]">
                <h3 className="text-[24px] font-medium text-[#033652] mb-1">
                  {project.name}
                </h3>
                <p className="text-[20px] font-normal text-[#4F7286]">
                  {project.location}
                </p>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  );
};

export default ProjectsSection;
